// The receipts whose slip shows LESS than the driver declared.
//
// THE COUNT OF DRIVERS IS THE FIRST THING ON THIS PANEL, ABOVE THE RAND, AND
// THAT IS DELIBERATE. At nine per cent of the backlog read this was 167 slips
// and R41,592, across 73 drivers out of roughly a hundred. A manager who reads
// the rand first has a fraud list. A manager who reads the breadth first has a
// process problem, which is what it is: when almost everybody does a thing, the
// thing they were asked to do is the defect.
//
// THE LITRES SORT IT AND THE GROUPS SAY SO IN WORDS. On that same sample the
// R41,592 was 99 slips where the declaration covers a second fill nobody
// photographed, 57 where this reader misread the slip, 14 with no litres
// printed, and FOUR that are genuinely a question about a person. The panel
// leads with those groups rather than with a list of names, because the list of
// names is only worth opening once the first two groups are out of it.
import { AlertCircle, Users } from 'lucide-react'
import { Chip } from '../interviews/Chip'
import { zar, num } from './cashModel'

export type Row = Record<string, any>

function groupOf(note: string): string {
  if (note.startsWith('The slip is')) return 'A second fill nobody photographed'
  if (note.startsWith("This slip's own price")) return 'The reading is doubtful, not the driver'
  if (note.startsWith('The slip carries no litres')) return 'No litres on the slip'
  if (note.startsWith('No pump price')) return 'No pump price on file for that day'
  return 'Genuinely open, a person decides'
}

const ORDER = [
  'Genuinely open, a person decides',
  'A second fill nobody photographed',
  'The reading is doubtful, not the driver',
  'No litres on the slip',
  'No pump price on file for that day',
]

export function OverDeclaredPanel({ rows, loading }: { rows: Row[]; loading: boolean }) {
  if (loading) return <p className="text-xs text-muted-foreground">Loading…</p>

  const drivers = new Set(rows.map((r) => String(r.driver_id))).size
  const total = rows.reduce((t, r) => t + Number(r.over_declared_zar ?? 0), 0)

  const groups = new Map<string, Row[]>()
  for (const r of rows) {
    const g = groupOf(String(r.what_it_looks_like ?? ''))
    groups.set(g, [...(groups.get(g) ?? []), r])
  }

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div>
        <h3 className="text-sm font-medium text-foreground">
          The slip shows less than was declared
        </h3>
        <p className="mt-1 flex items-start gap-2 text-xs text-muted-foreground">
          <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {num(rows.length)} slips across <strong className="font-medium">{num(drivers)} drivers</strong>,{' '}
          {zar(total)} between them. Read that driver count before the rand. A number this wide is a
          process, not a list of people: almost everybody doing a thing means the thing they were
          asked to do is the defect.
        </p>
      </div>

      {ORDER.filter((g) => groups.has(g)).map((g) => {
        const rs = groups.get(g)!
        const sum = rs.reduce((t, r) => t + Number(r.over_declared_zar ?? 0), 0)
        const needsAPerson = g.startsWith('Genuinely open')
        return (
          <div key={g}>
            <div className="mb-1 flex flex-wrap items-baseline gap-2">
              <span className="text-xs font-medium text-foreground">{g}</span>
              <Chip tone={needsAPerson ? 'amber' : 'neutral'}>
                {num(rs.length)} · {zar(sum)}
              </Chip>
            </div>
            <ul className="divide-y divide-border rounded-md border border-border">
              {rs.slice(0, needsAPerson ? 50 : 8).map((r) => (
                <li key={String(r.reading_id)} className="p-2 text-xs">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="tabular-nums text-muted-foreground">{String(r.txn_date ?? "")}</span>
                    <span className="font-medium">{r.driver_name}</span>
                    <span className="tabular-nums">
                      declared {zar(r.declared_zar)}, slip {zar(r.receipt_zar)}
                    </span>
                    <span className="ml-auto tabular-nums font-medium">
                      {zar(r.over_declared_zar)}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">{r.what_it_looks_like}</p>
                </li>
              ))}
              {rs.length > (needsAPerson ? 50 : 8) && (
                <li className="p-2 text-[11px] text-muted-foreground">
                  and {num(rs.length - (needsAPerson ? 50 : 8))} more in this group.
                </li>
              )}
            </ul>
          </div>
        )
      })}

      <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
        <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
        None of this deducts anything and none of it changes what anybody is paid. A slip that shows
        less than was declared is a photograph to go and ask for, and in most of these rows the
        missing thing is a second receipt rather than the money.
      </p>
    </div>
  )
}
